import { useEffect, useRef, useState } from "react";
import styles from "../../pages/BlogArticle.module.scss";
import { getSectionId } from "./articleUtils";
import { BlogMedia } from "./InstagramEmbed";
import InstagramLogo from "../InstagramLogo";
import { trackCtaClick } from "../../utils/analytics";
import { useLanguage } from "../../contexts/LanguageContext";
import { blogText } from "../../i18n/blog";

const getYoutubeEmbedUrl = (url) => {
  if (!url || typeof url !== "string") return "";

  try {
    const parsed = new URL(url);
    const host = parsed.hostname.replace("www.", "");

    if (host === "youtu.be") {
      const id = parsed.pathname.replace("/", "");
      return id ? `https://www.youtube.com/embed/${id}` : "";
    }

    if (host === "youtube.com" || host === "m.youtube.com") {
      if (parsed.pathname.startsWith("/shorts/")) {
        const id = parsed.pathname.split("/")[2];
        return id ? `https://www.youtube.com/embed/${id}` : "";
      }

      if (parsed.pathname.startsWith("/embed/")) {
        return url;
      }

      const id = parsed.searchParams.get("v");
      return id ? `https://www.youtube.com/embed/${id}` : "";
    }
  } catch {
    return "";
  }

  return "";
};

export function NormalArticleTemplate({ article }) {
  const { lang } = useLanguage();
  const t = blogText[lang] || blogText.JP;
  const points = Array.isArray(article.points) ? article.points : [];
  const galleryImages = Array.isArray(article.galleryImages)
    ? article.galleryImages.filter(Boolean)
    : [];
  const youtubeEmbedUrl = getYoutubeEmbedUrl(article.youtubeUrl);
  const guideInstagram = article.guideInstagram;
  const galleryRef = useRef(null);
  const [activePhotoIndex, setActivePhotoIndex] = useState(0);

  useEffect(() => {
    const gallery = galleryRef.current;
    if (!gallery || galleryImages.length === 0) return undefined;

    const updateActiveIndex = () => {
      const firstItem = gallery.firstElementChild;
      const itemWidth = firstItem?.getBoundingClientRect().width || 1;
      const gap = parseFloat(window.getComputedStyle(gallery).columnGap || "0");
      const index = Math.round(gallery.scrollLeft / (itemWidth + gap));

      setActivePhotoIndex(
        Math.min(Math.max(index, 0), galleryImages.length - 1),
      );
    };

    updateActiveIndex();
    gallery.addEventListener("scroll", updateActiveIndex, { passive: true });
    window.addEventListener("resize", updateActiveIndex);

    return () => {
      gallery.removeEventListener("scroll", updateActiveIndex);
      window.removeEventListener("resize", updateActiveIndex);
    };
  }, [galleryImages.length]);

  return (
    <div className={styles.pointList}>
      {(youtubeEmbedUrl || guideInstagram?.url || galleryImages.length > 0) && (
        <section className={styles.articleMediaBlock}>
          {youtubeEmbedUrl && (
            <div className={styles.articleYoutube}>
              <div className={styles.articleMediaHeading}>
                <span>Movie</span>
                <h2>{article.youtubeTitle || t.defaultYoutubeTitle}</h2>
              </div>

              <div className={styles.articleYoutubeFrame}>
                <iframe
                  src={youtubeEmbedUrl}
                  title={article.youtubeTitle || article.title}
                  loading="lazy"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </div>
            </div>
          )}

          {guideInstagram?.url && (
            <div className={styles.articleInstagramGuide}>
              <div className={styles.articleInstagramGuideHeader}>
                <div className={styles.articleInstagramGuideIcon}>
                  <InstagramLogo />
                </div>

                <div>
                  <span>Instagram</span>
                  <h2>
                    {guideInstagram.title || t.defaultGuideInstagramTitle}
                  </h2>
                  <p>{guideInstagram.text}</p>
                </div>
              </div>

              <div className={styles.articleInstagramGuideFrame}>
                <iframe
                  src={`${guideInstagram.url.replace(/\/?$/, "/")}embed`}
                  title={`${guideInstagram.handle || "Guide"} Instagram`}
                  loading="lazy"
                  allowTransparency="true"
                />
              </div>

              <a
                href={guideInstagram.url}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.articleInstagramGuideLink}
                onClick={() =>
                  trackCtaClick({
                    label: "guide_instagram_blog",
                    placement: "blog_article",
                    destination: guideInstagram.url,
                    destinationType: "instagram",
                  })
                }
              >
                {guideInstagram.handle ||
                  guideInstagram.cta ||
                  t.instagramButton}
              </a>
            </div>
          )}

          {galleryImages.length > 0 && (
            <>
              <div className={styles.articlePhotoGrid} ref={galleryRef}>
                {galleryImages.map((image, index) => (
                  <BlogMedia
                    key={`${article.slug}-gallery-${index}`}
                    media={image}
                    alt={`${article.title} ${t.articlePhotoAlt} ${index + 1}`}
                  />
                ))}
              </div>

              {galleryImages.length > 1 && (
                <div className={styles.articlePhotoDots} aria-hidden="true">
                  {galleryImages.map((image, index) => (
                    <span
                      key={`${article.slug}-gallery-dot-${index}`}
                      className={
                        index === activePhotoIndex ? styles.activePhotoDot : ""
                      }
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </section>
      )}

      {points.map((point, index) => (
        <NormalPoint
          key={point.number || index}
          point={point}
          index={index}
          article={article}
        />
      ))}
    </div>
  );
}

function NormalPoint({ point, index, article }) {
  const rows = Array.isArray(point.rows) ? point.rows : [];
  const hashtags = Array.isArray(point.hashtags) ? point.hashtags : [];

  return (
    <section id={getSectionId(index)} className={styles.pointItem}>
      <div className={styles.pointImage}>
        <BlogMedia
          media={
            point.instagramUrl
              ? {
                  instagramUrl: point.instagramUrl,
                }
              : point.media || point.image || article.mainImage
          }
          alt={point.title}
        />
      </div>

      <div className={styles.pointText}>
        <div className={styles.pointHeading}>
          <span>{point.number}</span>
          <h3>{point.title}</h3>
          {point.area && <small>{point.area}</small>}
        </div>

        {point.text && <p>{point.text}</p>}

        {rows.length > 0 && (
          <dl>
            {rows.map(([label, value]) => (
              <div key={label}>
                <dt>{label}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>
        )}

        {hashtags.length > 0 && (
          <div className={styles.hashtags}>
            {hashtags.map((tag) => (
              <span key={tag}>{tag}</span>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
